import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Ruler, Save, Scissors, Search, X } from 'lucide-react';
import {
  fetchItem, createItem, updateItem, listMeasurements,
  listWorkRates, createWorkRate, attachWorkRateToItem, detachWorkRateFromItem,
} from '../api/resources';
import { useToast } from '../context/ToastContext';
import PhotoUpload from '../components/ui/PhotoUpload';
import Spinner from '../components/ui/Spinner';
import { inr } from '../utils/format';
import { ZONE_LABEL, ZONE_ORDER } from './portal/shared';

const GST_RATES = [0, 5, 12, 18, 28];

const EMPTY = {
  name: '', photo: '', customerRate: null, cuttingMasterRate: null,
  sewingWorkerRate: null, materialExpenseRate: null, taxRate: 5, status: 'ACTIVE',
};

/**
 * Create or edit one tailoring item on a single screen, desktop-software
 * style: a toolbar on top, item details on the left, the measurements this
 * garment needs on the right. The page itself never scrolls — only a long
 * measurement list scrolls inside its own panel.
 *
 * Deliberately small: name, GST, rates, custom work rates and measurements.
 * HSN/SAC and style types were dropped from this screen at the shop's
 * request, to keep it easy for non-technical staff.
 *
 * Ctrl+S saves.
 */
export default function TailoringItemForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const nav = useNavigate();
  const qc = useQueryClient();
  const { success, error } = useToast();

  const [form, setForm] = useState(EMPTY);
  const [loaded, setLoaded] = useState(!isEdit);
  const [rateIds, setRateIds] = useState(() => new Set());
  const [savedRateIds, setSavedRateIds] = useState(() => new Set());
  const [metricIds, setMetricIds] = useState(() => new Set());
  // What this item is made of, when it is a set: "Coat Pant" is a Coat and a
  // Pant. The parts belong to this item alone and are never listed as items
  // of their own. Each keeps its own price, so the set costs what its parts
  // cost. `key` is only for React — a new part has no id until it is saved.
  const [parts, setParts] = useState([]);
  const editPart = (key, patch) => setParts((list) =>
    list.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const { data: metricData } = useQuery({ queryKey: ['measurements', ''], queryFn: () => listMeasurements('') });
  const metrics = metricData?.measurements || [];
  const { data: rateData = [] } = useQuery({ queryKey: ['workRates'], queryFn: listWorkRates });
  const allRates = rateData || [];

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { tailoringItem: it } = await fetchItem(id);
        setForm({
          name: it.name || '', photo: it.photo || '',
          customerRate: it.customerRate, cuttingMasterRate: it.cuttingMasterRate,
          sewingWorkerRate: it.sewingWorkerRate, materialExpenseRate: it.materialExpenseRate,
          taxRate: Number(it.taxRate ?? 0), status: it.status || 'ACTIVE',
        });
        const rates = new Set((it.customWorkRates || []).map((w) => w.id));
        setRateIds(rates);
        setSavedRateIds(rates);
        setMetricIds(new Set((it.measurements || []).map((m) => m.id)));
        setParts((it.subItems || []).map((c) => ({
          key: `s${c.id}`, id: c.id, name: c.name || '', customerRate: c.customerRate ?? '',
        })));
      } catch {
        error('Could not load this item.');
      } finally {
        setLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const num = (v) => (v === '' ? null : parseFloat(v));

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        measurementIds: [...metricIds],
        subItems: parts
          .filter((p) => p.name.trim())
          .map((p) => ({ id: p.id, name: p.name.trim(), customerRate: p.customerRate })),
      };
      const res = isEdit ? await updateItem(Number(id), body) : await createItem(body);
      const savedId = res?.tailoringItem?.id ?? (isEdit ? Number(id) : null);
      if (!savedId) return res;

      // Work-rate links are their own endpoints. The item is already saved,
      // so a failed link is reported without calling the whole save failed.
      const toAttach = [...rateIds].filter((x) => !savedRateIds.has(x));
      const toDetach = [...savedRateIds].filter((x) => !rateIds.has(x));
      const results = await Promise.allSettled([
        ...toAttach.map((rid) => attachWorkRateToItem(savedId, rid)),
        ...toDetach.map((rid) => detachWorkRateFromItem(savedId, rid)),
      ]);
      const failed = results.filter((r) => r.status === 'rejected').length;
      return failed
        ? { ...res, message: `${res.message} (${failed} work rate link${failed === 1 ? '' : 's'} failed)` }
        : res;
    },
    onSuccess: (d) => {
      success(d?.message || 'Saved.');
      qc.invalidateQueries({ queryKey: ['items'] });
      qc.invalidateQueries({ queryKey: ['workRates'] });
      nav('/tailoring-items');
    },
    onError: (e) => error(e.response?.data?.message || 'Save failed'),
  });

  const canSave = !!form.name.trim() && !save.isPending;

  // Ctrl+S / Cmd+S saves, like desktop software.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (canSave) save.mutate();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canSave, save]);

  // An existing item may carry a rate outside the usual slabs; keep it
  // selectable rather than silently snapping it to a different one.
  const gstOptions = GST_RATES.includes(Number(form.taxRate))
    ? GST_RATES
    : [...GST_RATES, Number(form.taxRate)].sort((a, b) => a - b);

  if (!loaded) return <Spinner label="Loading item…" />;

  return (
    <div className="flex h-[calc(100vh-6.5rem)] min-h-[32rem] flex-col gap-3">
      {/* ---- toolbar ---- */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-4 py-2.5">
        <Scissors size={18} className="text-brand-600" />
        <h1 className="mr-auto truncate text-lg font-bold text-content">
          {isEdit ? `Edit Item — ${form.name || ''}` : 'New Item'}
        </h1>
        <span className="hidden text-xs text-content-subtle md:inline">Ctrl+S to save</span>
        <button className="btn-outline" onClick={() => nav('/tailoring-items')}>
          <X size={15} /> Cancel
        </button>
        <button className="btn-primary" disabled={!canSave} onClick={() => save.mutate()}>
          <Save size={15} /> {save.isPending ? 'Saving…' : 'Save Item'}
        </button>
      </div>

      {/* ---- body ---- */}
      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[24rem_1fr]">
        {/* left: the item */}
        <section className="min-h-0 overflow-y-auto rounded-lg border border-line bg-surface p-4">
            <p className="panel-title">Item</p>
            <label className="block">
              <span className="field-label">Item name <span className="text-danger-600">*</span></span>
              <input
                className="input text-base font-semibold"
                value={form.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="e.g. Shirt"
                autoFocus
              />
            </label>

            <div className="mt-3 grid grid-cols-2 gap-3">
              <label className="block">
                <span className="field-label">GST</span>
                <select className="input" value={Number(form.taxRate)} onChange={(e) => set({ taxRate: Number(e.target.value) })}>
                  {gstOptions.map((r) => <option key={r} value={r}>{r}%</option>)}
                </select>
              </label>
              <label className="block">
                <span className="field-label">Status</span>
                <select className="input" value={form.status} onChange={(e) => set({ status: e.target.value })}>
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                </select>
              </label>
            </div>

            <div className="mt-3">
              <span className="field-label">Photo (optional)</span>
              <PhotoUpload value={form.photo} onChange={(photo) => set({ photo })} size="h-12 w-12" />
            </div>

            {/* A set: one thing on the price list, several garments on the
                floor. The parts belong to this item — they are not other
                catalogue garments — so they are named and priced right here,
                and they never show up in the items list on their own. */}
            <p className="panel-title mt-5 border-t border-line pt-4">Parts of a set</p>
            <p className="-mt-2 mb-2 text-xs text-content-muted">
              Leave this empty for an ordinary garment. Add parts to make this a set —
              “Coat Pant” holding a Coat and a Pant. Each part keeps its own price, and
              on an order each becomes its own garment with its own tailor. A set is
              sold whole: no part can be taken off an order by itself.
            </p>

            {parts.length > 0 && (
              <div className="mb-2 space-y-1.5">
                {parts.map((p, i) => (
                  <div key={p.key} className="flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2">
                    <span className="shrink-0 text-2xs font-semibold text-content-subtle">{i + 1}.</span>
                    <input
                      className="input input-sm min-w-0 flex-1 text-sm"
                      placeholder="Part name, e.g. Coat"
                      value={p.name}
                      onChange={(e) => editPart(p.key, { name: e.target.value })}
                    />
                    <label className="flex items-center gap-1.5 text-2xs text-content-muted">
                      Price
                      <MoneyInput
                        value={p.customerRate}
                        onChange={(v) => editPart(p.key, { customerRate: v })}
                        parse={num}
                        className="!w-24"
                      />
                    </label>
                    <button
                      type="button"
                      className="btn-icon hover:bg-danger-50 hover:text-danger-600"
                      title={p.name ? `Remove ${p.name}` : 'Remove this part'}
                      onClick={() => setParts((list) => list.filter((x) => x.key !== p.key))}
                    >
                      <X size={15} />
                    </button>
                  </div>
                ))}
                <p className="text-right text-xs font-semibold text-content">
                  Set price {inr(parts.reduce((s, p) => s + Number(p.customerRate || 0), 0))}
                </p>
              </div>
            )}

            <button
              type="button"
              className="btn-outline btn-sm"
              onClick={() => setParts((list) => [...list, {
                key: `n${Date.now()}${list.length}`, id: null, name: '', customerRate: '',
              }])}
            >
              <Plus size={14} /> Add a part
            </button>

            <p className="panel-title mt-5 border-t border-line pt-4">Rates</p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="field-label">Customer price</span>
                {/* A set has no price of its own — it costs what its parts cost. */}
                {parts.length ? (
                  <p className="input flex items-center !text-sm text-content-muted">
                    {inr(parts.reduce((s, p) => s + Number(p.customerRate || 0), 0))} from its parts
                  </p>
                ) : (
                  <MoneyInput value={form.customerRate} onChange={(v) => set({ customerRate: v })} parse={num} strong />
                )}
              </label>
              <label className="block">
                <span className="field-label">Material cost</span>
                <MoneyInput value={form.materialExpenseRate} onChange={(v) => set({ materialExpenseRate: v })} parse={num} />
              </label>
              <label className="block">
                <span className="field-label">Cutting (worker)</span>
                <MoneyInput value={form.cuttingMasterRate} onChange={(v) => set({ cuttingMasterRate: v })} parse={num} />
              </label>
              <label className="block">
                <span className="field-label">Sewing (worker)</span>
                <MoneyInput value={form.sewingWorkerRate} onChange={(v) => set({ sewingWorkerRate: v })} parse={num} />
              </label>
            </div>

            <CustomRates
              allRates={allRates}
              selected={rateIds}
              onToggle={(rid) => setRateIds((p) => { const n = new Set(p); n.has(rid) ? n.delete(rid) : n.add(rid); return n; })}
              onCreated={(r) => { qc.invalidateQueries({ queryKey: ['workRates'] }); setRateIds((p) => new Set(p).add(r.id)); }}
            />
        </section>

        {/* right: measurements */}
        <MeasurementPicker metrics={metrics} selected={metricIds} onChange={setMetricIds} />
      </div>
    </div>
  );
}

/* ---------------- ₹ input ---------------- */
function MoneyInput({ value, onChange, parse, strong, className = '' }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-content-subtle">₹</span>
      <input
        type="number"
        step="0.01"
        min={0}
        inputMode="decimal"
        className={`input pl-7 text-right tabular-nums ${strong ? 'text-base font-semibold' : ''} ${className}`}
        value={value ?? ''}
        placeholder="0"
        onChange={(e) => onChange(parse(e.target.value))}
      />
    </div>
  );
}

/* ---------------- custom work rates ---------------- */
function CustomRates({ allRates, selected, onToggle, onCreated }) {
  const { success, error } = useToast();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) return error('Enter a name for the extra work.');
    if (amount === '' || Number.isNaN(Number(amount))) return error('Enter the amount.');
    try {
      setBusy(true);
      const created = await createWorkRate({ name: name.trim(), rate: Number(amount) });
      onCreated(created);
      setName(''); setAmount(''); setAdding(false);
      success(`"${created.name}" added.`);
    } catch (e) {
      error(e.response?.data?.error || 'Could not add that work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-line pt-3">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="field-label !mb-0">Extra work (optional)</span>
        {!adding && (
          <button type="button" className="text-xs font-semibold text-brand-700 hover:underline" onClick={() => setAdding(true)}>
            + New
          </button>
        )}
      </div>

      {adding && (
        <div className="mb-2 flex items-center gap-1.5">
          <input className="input !py-1.5 min-w-0 flex-1 text-sm" placeholder="e.g. Embroidery" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <input type="number" step="0.01" className="input !py-1.5 w-20 text-right text-sm" placeholder="₹" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={create}>{busy ? '…' : 'Add'}</button>
          <button type="button" className="btn-icon h-8 w-8" title="Cancel" onClick={() => { setAdding(false); setName(''); setAmount(''); }}><X size={14} /></button>
        </div>
      )}

      {allRates.length === 0 ? (
        !adding && <p className="text-xs text-content-subtle">None yet.</p>
      ) : (
        <div className="space-y-1">
          {allRates.map((r) => (
            <label key={r.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="text-brand-700" checked={selected.has(r.id)} onChange={() => onToggle(r.id)} />
              <span className="flex-1 truncate">{r.name}</span>
              <span className="tabular-nums text-content-subtle">{inr(r.rateDecimal)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- measurements for this item ---------------- */
function MeasurementPicker({ metrics, selected, onChange }) {
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ZONE_ORDER
      .map((z) => ({
        zone: z,
        rows: metrics.filter((m) => (m.zone || 'FULL') === z && (!q || m.name.toLowerCase().includes(q))),
      }))
      .filter((g) => g.rows.length);
  }, [metrics, query]);

  const toggle = (mid) => onChange((prev) => {
    const next = new Set(prev);
    next.has(mid) ? next.delete(mid) : next.add(mid);
    return next;
  });
  const setMany = (ids, on) => onChange((prev) => {
    const next = new Set(prev);
    ids.forEach((mid) => (on ? next.add(mid) : next.delete(mid)));
    return next;
  });

  return (
    <section className="flex min-h-0 flex-col rounded-lg border border-line bg-surface">
      {/* header */}
      <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-2.5">
        <Ruler size={16} className="text-brand-600" />
        <div className="mr-auto">
          <p className="text-sm font-bold text-content">Measurements for this item</p>
          <p className="text-xs text-content-subtle">
            {selected.size
              ? `${selected.size} of ${metrics.length} ticked — only these will be asked on orders later.`
              : 'Nothing ticked — every measurement stays available.'}
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input className="input !py-1.5 w-44 pl-8 text-sm" placeholder="Find…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <button type="button" className="btn-outline btn-sm" onClick={() => setMany(metrics.map((m) => m.id), true)}>Tick all</button>
        <button type="button" className="btn-outline btn-sm" onClick={() => onChange(new Set())}>Clear</button>
      </div>

      {/* body — the only part of the screen that may scroll */}
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {metrics.length === 0 ? (
          <p className="py-10 text-center text-sm text-content-subtle">
            No measurements defined yet — add them under Common Measurements first.
          </p>
        ) : groups.length === 0 ? (
          <p className="py-10 text-center text-sm text-content-subtle">Nothing matches “{query}”.</p>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => {
              const ids = g.rows.map((m) => m.id);
              const onCount = ids.filter((mid) => selected.has(mid)).length;
              const allOn = onCount === ids.length;
              return (
                <div key={g.zone}>
                  <div className="mb-1.5 flex items-center gap-3 border-b border-line pb-1">
                    <p className="text-2xs font-bold uppercase tracking-wider text-content-subtle">
                      {ZONE_LABEL[g.zone]} <span className="font-semibold normal-case tracking-normal">· {onCount}/{ids.length}</span>
                    </p>
                    <button type="button" className="ml-auto text-2xs font-semibold text-brand-700 hover:underline" onClick={() => setMany(ids, !allOn)}>
                      {allOn ? 'Untick all' : 'Tick all'}
                    </button>
                  </div>
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-1.5">
                    {g.rows.map((m) => {
                      const on = selected.has(m.id);
                      return (
                        <label
                          key={m.id}
                          className={`flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-2 text-sm transition-colors ${on ? 'border-brand-300 bg-brand-50 font-medium text-brand-800' : 'border-line hover:bg-surface-hover'}`}
                        >
                          <input type="checkbox" className="text-brand-700" checked={on} onChange={() => toggle(m.id)} />
                          <span className="truncate" title={m.name}>{m.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
